import { LegacySuggestionsSchema, NewSuggestionsSchema, SuggestionSchema } from '.';
import type {
    LegacySuggestionsStorage,
    NewSuggestionsStorage,
    StoredSuggestion,
    Suggestion,
    SuggestionsService,
} from '.';
import { injectable, inject } from 'src/features/ioc';
import type { CurrentUserModel } from 'src/models/CurrentUserModel';
import { invalidateSuggestionsForKey } from 'src/queries/topMenuItems';
import type { SyncStorageService } from 'src/services/SyncStorageService';
import type { IsomorphicBindings } from 'src/types/bindings';

@injectable()
export class SuggestionsServiceImpl implements SuggestionsService {
    #MAX_PER_QUERY: number;

    constructor(
        @inject('QueryClient') private queryClient: IsomorphicBindings['QueryClient'],
        @inject('ConfigService') private configService: IsomorphicBindings['ConfigService'],
        @inject('CurrentUserModel') private currentUserModel: CurrentUserModel,
        @inject('SyncStorageService') private storage: SyncStorageService,
        maxPerQuery = 20,
    ) {
        this.#MAX_PER_QUERY = maxPerQuery;
    }

    #getCurrentUserId(): string {
        if (this.currentUserModel.isSuperUserModeEnabled.value) {
            return 'superuser';
        }

        // userEmail already reflects the effective user (masquerading or regular)
        return this.currentUserModel.userEmail.value ?? 'unknown';
    }

    #getStoredData(): NewSuggestionsStorage {
        const currentUserId = this.#getCurrentUserId();

        // Try new storage key first
        let storedData = this.storage.getItem(this.#storageKey);

        // If not found, try old "top_items" key for migration
        if (!storedData) {
            const oldKey = `top_items_${this.configService.environmentName}`;
            storedData = this.storage.getItem(oldKey);

            // If we found old data, we'll migrate it and remove the old key
            if (storedData) {
                const migrated = this.#parseAndMigrateData(storedData, currentUserId);
                // Store in new location and clean up old key
                this.#storeData(migrated);
                this.storage.removeItem(oldKey);
                return migrated;
            }

            return { version: 1, userId: currentUserId, byType: {} };
        }

        return this.#parseAndMigrateData(storedData, currentUserId);
    }

    #parseAndMigrateData(storedData: string, currentUserId: string): NewSuggestionsStorage {
        let parsed: unknown;
        try {
            parsed = JSON.parse(storedData);
        } catch {
            // Invalid JSON, return empty
            return { version: 1, userId: currentUserId, byType: {} };
        }

        // Try parsing as new format (version 1 with userId) first
        try {
            const data = NewSuggestionsSchema.parse(parsed);
            // Check if the stored data is for the current user
            if (data.userId !== currentUserId) {
                // Different user, return empty data for current user
                return { version: 1, userId: currentUserId, byType: {} };
            }
            return data;
        } catch {
            // Not new format, try legacy
        }

        // Try parsing as legacy format and migrate
        try {
            const legacy = LegacySuggestionsSchema.parse(parsed);
            return this.#migrateLegacyData(legacy, currentUserId);
        } catch {
            // Invalid data, return empty
        }

        // Invalid data, return empty
        return { version: 1, userId: currentUserId, byType: {} };
    }

    #migrateLegacyData(legacy: LegacySuggestionsStorage, currentUserId: string): NewSuggestionsStorage {
        const byType: Record<Suggestion['type'], StoredSuggestion[]> = {} as Record<
            Suggestion['type'],
            StoredSuggestion[]
        >;
        const now = Date.now();

        // Process recentlyAccessed items
        for (const item of legacy.recentlyAccessed) {
            if (!byType[item.type]) {
                byType[item.type] = [];
            }
            const existing = byType[item.type]!.find((i) => i.id === item.id);
            if (existing) {
                existing.accessedAt = now;
            } else {
                byType[item.type]!.push({ id: item.id, userId: currentUserId, accessedAt: now, createdAt: null });
            }
        }

        // Process recentlyCreated items
        for (const item of legacy.recentlyCreated) {
            if (!byType[item.type]) {
                byType[item.type] = [];
            }
            const existing = byType[item.type]!.find((i) => i.id === item.id);
            if (existing) {
                existing.createdAt = now;
            } else {
                byType[item.type]!.push({ id: item.id, userId: currentUserId, accessedAt: null, createdAt: now });
            }
        }

        return { version: 1, userId: currentUserId, byType };
    }

    #storeData(data: NewSuggestionsStorage): void {
        this.storage.setItem(this.#storageKey, JSON.stringify(data));
        invalidateSuggestionsForKey(this.queryClient);
    }

    getItems(opts: { types?: Suggestion['type'][]; query: 'accessedAt' | 'createdAt'; limit?: number }): Suggestion[] {
        const data = this.#getStoredData();
        const { types, query, limit } = opts;
        const currentUserId = this.#getCurrentUserId();

        // Collect items from relevant types
        const typesToQuery = types?.length ? types : (Object.keys(data.byType) as Suggestion['type'][]);
        const allItems: StoredSuggestion[] = [];

        for (const type of typesToQuery) {
            const typeArray = data.byType[type];
            if (typeArray) {
                allItems.push(...typeArray.map((item) => ({ ...item, type })));
            }
        }

        // Filter by current user ID and query dimension
        const filtered = allItems.filter((item) => item.userId === currentUserId && item[query] !== null);
        const sorted = filtered.sort((a, b) => b[query]! - a[query]!);

        // Apply limit if specified
        const limited = limit ? sorted.slice(0, limit) : sorted;

        // Map to Suggestion format
        return limited.map((item) => ({
            id: item.id,
            type: (item as StoredSuggestion & { type: Suggestion['type'] }).type,
        }));
    }

    addRecentlyAccessed(newItem: Suggestion): void {
        const data = this.#getStoredData();
        const { type, id } = SuggestionSchema.parse(newItem);
        const currentUserId = this.#getCurrentUserId();

        if (!data.byType[type]) {
            data.byType[type] = [];
        }

        const typeArray = data.byType[type];
        const existing = typeArray.find((item) => item.id === id && item.userId === currentUserId);

        if (existing) {
            existing.accessedAt = Date.now();
        } else {
            typeArray.push({ id, userId: currentUserId, accessedAt: Date.now(), createdAt: null });
        }

        data.byType[type] = pruneTypeArray(typeArray, this.#MAX_PER_QUERY);
        this.#storeData(data);
    }

    addRecentlyCreated(newItem: Suggestion): void {
        const data = this.#getStoredData();
        const { type, id } = SuggestionSchema.parse(newItem);
        const currentUserId = this.#getCurrentUserId();

        if (!data.byType[type]) {
            data.byType[type] = [];
        }

        const typeArray = data.byType[type];
        const existing = typeArray.find((item) => item.id === id && item.userId === currentUserId);

        if (existing) {
            existing.createdAt = Date.now();
        } else {
            typeArray.push({ id, userId: currentUserId, accessedAt: null, createdAt: Date.now() });
        }

        data.byType[type] = pruneTypeArray(typeArray, this.#MAX_PER_QUERY);
        this.#storeData(data);
    }

    delete(id: string): void {
        const data = this.#getStoredData();
        const currentUserId = this.#getCurrentUserId();

        // Remove item from all type arrays (only for current user)
        for (const type of Object.keys(data.byType) as Suggestion['type'][]) {
            const typeArray = data.byType[type];
            if (typeArray) {
                data.byType[type] = typeArray.filter((item) => !(item.id === id && item.userId === currentUserId));
            }
        }

        this.#storeData(data);
    }

    get #storageKey() {
        const userId = this.#getCurrentUserId();
        return `suggestions_${this.configService.environmentName}_${userId}`;
    }
}

/**
 * Prunes an array of items to keep the union of:
 * - Top N by accessedAt (where accessedAt !== null)
 * - Top N by createdAt (where createdAt !== null)
 *
 * This ensures both query dimensions can return up to N results even with no overlap.
 * Maximum storage: 2N items (typically less due to overlap).
 */
const pruneTypeArray = (items: StoredSuggestion[], maxPerQuery: number): StoredSuggestion[] => {
    const topByAccess = items
        .filter((i) => i.accessedAt !== null)
        .sort((a, b) => b.accessedAt! - a.accessedAt!)
        .slice(0, maxPerQuery);

    const topByCreate = items
        .filter((i) => i.createdAt !== null)
        .sort((a, b) => b.createdAt! - a.createdAt!)
        .slice(0, maxPerQuery);

    // Build union set of IDs to keep
    const idsToKeep = new Set([...topByAccess.map((i) => i.id), ...topByCreate.map((i) => i.id)]);

    return items.filter((i) => idsToKeep.has(i.id));
};
